export interface TextualSearchValidationResult {
  isValid: boolean;
  errorMessageKey?: string;
  query: string;
}

export interface TextualSearchValidatorOptions {
  wildcardCaseInsensitive?: boolean;
}

const REGEX_PATTERNS = {
  ALLOWED_CHARS: /[^a-zA-Z\u00C0-\u00FF0-9_+\-:.()"*&|{}[\]^~\\@#/$'= ]/,
  ASTERISK_MISUSE: /^[*]*$|\s\*|^\*[^\s]|\*{2,}/,
  AMPERSANDS_PRESENCE: /&&/,
  AMPERSANDS_STRUCTURE: /^[a-z\u00C0-\u00FFA-Z0-9_+\-:.()"*?|!{}[\]^~\\@#/$%'=]+(?:(?: && |\s+)[a-z\u00C0-\u00FFA-Z0-9_+\-:.()"*?|!{}[\]^~\\@#/$%'=]+)+ *$/,
  CARET_MISUSE: /([^\\]\^([^\s]*[^0-9.]+))|([^\\]\^$)/,
  SQUIGGLE_MISUSE: /[^\\]~[^\s]*[^0-9\s]+/,
  EXCLAMATION_STRUCTURE: /^[^!]*$|^[a-z\u00C0-\u00FFA-Z0-9_+\-:.()"*?&|!{}[\]^~\\@#/$%'=]+(?: ! [a-z\u00C0-\u00FFA-Z0-9_+\-:.()"*?&|!{}[\]^~\\@#/$%'=]+)* *$/,
  QUESTION_MARK_MISUSE: /(^(\?))|([^a-z\u00C0-\u00FFA-Z0-9_+\-:.()"*?&|!{}[\]^~\\@#/$%'=]\?+)/,
  PARENTHESES_EMPTY: /\(\)/,
  PLUS_MINUS_STRUCTURE: /^[+-]?[a-z\u00C0-\u00FFA-Z0-9_:.()"*?&|!{}[\]^~\\@#/$%'=]+(?:(?: +[+-]?|[+-])[a-z\u00C0-\u00FFA-Z0-9_:.()"*?&|!{}[\]^~\\@#/$%'=]+)*(?:[a-zA-Z]\+)?$/,
  KEYWORDS_PRESENCE: /AND|OR|NOT/,
  KEYWORDS_STRUCTURE: /^[a-z\u00C0-\u00FFA-Z0-9_+\-:.()"*?&|!{}[\]^~\\@/#$%'=]+(?:(?:\s*(?:AND |OR |AND NOT |NOT )|\s+)[a-z\u00C0-\u00FFA-Z0-9_+\-:.()"*?&|!{}[\]^~\\@/#$%'=]+)+ *$/,
  KEYWORDS_ISOLATED: /(?:^(?:AND|OR|AND NOT|NOT) )|(?:(?:AND|OR|AND NOT|NOT) *$)/,
  QUOTES_EMPTY: /""/,
  COLON_MISUSE: /[^\\\s]:\s|[^\\\s]:$|\s[^\\]?:|^[^\\\s]?:/
};

type ValidationRule = (query: string) => string | null;

const checkAllowedCharacters: ValidationRule = (query) => {
  return REGEX_PATTERNS.ALLOWED_CHARS.exec(query) ? 'globalFilter.errors.invalidCharacters' : null;
};

const checkAsterisk: ValidationRule = (query) => {
  return REGEX_PATTERNS.ASTERISK_MISUSE.exec(query) ? 'globalFilter.errors.invalidAsterisk' : null;
};

const checkAmpersands: ValidationRule = (query) => {
  if (REGEX_PATTERNS.AMPERSANDS_PRESENCE.exec(query)) {
    if (!REGEX_PATTERNS.AMPERSANDS_STRUCTURE.exec(query)) {
      return 'globalFilter.errors.invalidAmpersands';
    }
  }
  return null;
};

const checkCaret: ValidationRule = (query) => {
  return REGEX_PATTERNS.CARET_MISUSE.exec(query) ? 'globalFilter.errors.invalidCaret' : null;
};

const checkSquiggle: ValidationRule = (query) => {
  return REGEX_PATTERNS.SQUIGGLE_MISUSE.exec(query) ? 'globalFilter.errors.invalidSquiggle' : null;
};

const checkExclamationMark: ValidationRule = (query) => {
  if (!REGEX_PATTERNS.EXCLAMATION_STRUCTURE.exec(query)) {
    return 'globalFilter.errors.invalidExclamation';
  }
  return null;
};

const checkQuestionMark: ValidationRule = (query) => {
  return REGEX_PATTERNS.QUESTION_MARK_MISUSE.exec(query) ? 'globalFilter.errors.invalidQuestionMark' : null;
};

const checkParentheses: ValidationRule = (query) => {
  const leftCount = query.split('(').length - 1;
  const rightCount = query.split(')').length - 1;

  if (leftCount !== rightCount) return 'globalFilter.errors.unclosedParentheses';
  if (REGEX_PATTERNS.PARENTHESES_EMPTY.exec(query)) return 'globalFilter.errors.emptyParentheses';
  
  return null;
};

const checkPlusMinus: ValidationRule = (query) => {
  if (!REGEX_PATTERNS.PLUS_MINUS_STRUCTURE.exec(query.trim())) {
    return 'globalFilter.errors.invalidPlusMinus';
  }
  return null;
};

const checkKeywords: ValidationRule = (query) => {
  if (REGEX_PATTERNS.KEYWORDS_PRESENCE.exec(query)) {
    if (!REGEX_PATTERNS.KEYWORDS_STRUCTURE.exec(query) || REGEX_PATTERNS.KEYWORDS_ISOLATED.exec(query)) {
      return 'globalFilter.errors.invalidKeywords';
    }
  }
  return null;
};

const checkQuotes: ValidationRule = (query) => {
  const quoteCount = query.split('"').length - 1;

  if (quoteCount % 2 !== 0) return 'globalFilter.errors.unclosedQuotes';
  if (REGEX_PATTERNS.QUOTES_EMPTY.exec(query)) return 'globalFilter.errors.emptyQuotes';
  
  return null;
};

const checkColon: ValidationRule = (query) => {
  return REGEX_PATTERNS.COLON_MISUSE.exec(query) ? 'globalFilter.errors.invalidColon' : null;
};

const validationRules: ValidationRule[] = [
  checkAllowedCharacters,
  checkAsterisk,
  checkAmpersands,
  checkCaret,
  checkSquiggle,
  checkExclamationMark,
  checkQuestionMark,
  checkParentheses,
  checkPlusMinus,
  checkKeywords,
  checkQuotes,
  checkColon
];

export function validateTextualSearch(
  query: string,
  options?: TextualSearchValidatorOptions
): TextualSearchValidationResult {
  if (!query || query.trim().length === 0) {
    return { isValid: false, errorMessageKey: 'globalFilter.errors.emptySearch', query };
  }

  const cleanQuery = query.replaceAll(/\\./g, '');
  const finalOptions = options ?? { wildcardCaseInsensitive: true };

  for (const rule of validationRules) {
    const errorKey = rule(cleanQuery);
    if (errorKey) {
      return { isValid: false, errorMessageKey: errorKey, query };
    }
  }

  let finalQuery = query;
  if (finalOptions.wildcardCaseInsensitive && finalQuery.includes('*')) {
    finalQuery = formatWildcardCase(finalQuery);
  }

  return { isValid: true, query: finalQuery };
}

function formatWildcardCase(query: string): string {
  const colonIndex = query.indexOf(':');

  if (colonIndex === -1) {
    return query.toLowerCase();
  }

  const fieldPrefix = query.substring(0, colonIndex);
  const searchTerms = query.substring(colonIndex).toLowerCase();

  return `${fieldPrefix}${searchTerms}`;
}